import * as React from 'react';
import { ListBoxItem as AriaListBoxItem, Text as AriaText } from 'react-aria-components';

import { CheckIcon } from '../../icons';
import { cx } from '../../utils/cx';
import { isReactComponent } from '../../utils/isReactComponent';
import Avatar from '../Avatar/Avatar';

import { SelectContext } from './Select';

import type { SelectItemType } from './Select';
import type { ListBoxItemProps as AriaListBoxItemProps } from 'react-aria-components';

const sizes = {
  sm: 'p-2 pr-2.5',
  md: 'p-2.5 pl-2',
};

interface ISelectItemProps extends Omit<AriaListBoxItemProps<SelectItemType>, 'id'>, SelectItemType {}

const SelectItem = ({
  label,
  id,
  value,
  avatarUrl,
  supportingText,
  isDisabled,
  icon: Icon,
  className,
  children,
  ...props
}: ISelectItemProps) => {
  const { size } = React.useContext(SelectContext);

  const labelOrChildren = label || (typeof children === 'string' ? children : '');
  const textValue = supportingText ? labelOrChildren + ' ' + supportingText : labelOrChildren;

  return (
    <AriaListBoxItem
      id={id}
      value={
        value ?? {
          id,
          label: labelOrChildren,
          avatarUrl,
          supportingText,
          isDisabled,
          icon: Icon,
        }
      }
      textValue={textValue}
      isDisabled={isDisabled}
      {...props}
      className={(state) =>
        cx('outline-hidden w-full px-1.5 py-px', typeof className === 'function' ? className(state) : className)
      }
    >
      {(state) => (
        <div
          className={cx(
            'outline-hidden flex cursor-pointer select-none items-center gap-2 rounded-md',
            state.isSelected && 'bg-active',
            state.isDisabled && 'cursor-not-allowed',
            state.isFocused && 'bg-primary_hover',
            state.isFocusVisible && 'ring-focus-ring ring-2 ring-inset',

            // Icon styles
            '*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary',
            state.isDisabled && '*:data-icon:text-fg-disabled',

            sizes[size]
          )}
        >
          {avatarUrl ? (
            <Avatar aria-hidden="true" size="xs" src={avatarUrl} alt={label} />
          ) : isReactComponent(Icon) ? (
            <Icon data-icon aria-hidden="true" />
          ) : React.isValidElement(Icon) ? (
            Icon
          ) : null}

          <div className="flex w-full min-w-0 flex-1 flex-wrap gap-x-2">
            <AriaText
              slot="label"
              className={cx(
                'text-md text-primary truncate whitespace-nowrap font-medium',
                state.isDisabled && 'text-disabled'
              )}
            >
              {label || (typeof children === 'function' ? children(state) : children)}
            </AriaText>

            {supportingText && (
              <AriaText
                slot="description"
                className={cx('text-md text-tertiary whitespace-nowrap', state.isDisabled && 'text-disabled')}
              >
                {supportingText}
              </AriaText>
            )}
          </div>

          {state.isSelected && (
            <CheckIcon
              aria-hidden="true"
              className={cx(
                'text-fg-brand-primary ml-auto',
                size === 'sm' ? 'size-4 stroke-[2.5px]' : 'size-5',
                state.isDisabled && 'text-fg-disabled'
              )}
            />
          )}
        </div>
      )}
    </AriaListBoxItem>
  );
};

export default SelectItem;
